import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X, Send, Sparkles, Wind, BookOpen, Loader2, Share2 } from 'lucide-react';
import ShareSheet from './ShareSheet';
import { authHeaders } from '../context/AuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const SUGGESTIONS = [
  "I'm overwhelmed at work",
  "I can't fall asleep",
  "My chest feels tight",
  "I'm exhausted but wired",
];

const ReflectionCoachOverlay = ({ open, onClose }) => {
  // Conversation: array of { id, role: 'user' | 'coach' | 'error', text, response? }
  const [conversation, setConversation] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [shareAffirmation, setShareAffirmation] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Reset on close (ephemeral — no persistence per product decision)
  useEffect(() => {
    if (!open) {
      setConversation([]);
      setDraft('');
      setLoading(false);
    } else {
      // Focus the input shortly after open
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation, loading]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = (text ?? draft).trim();
      if (!trimmed || loading) return;
      const userMsgId = `u-${Date.now()}`;
      setConversation((prev) => [...prev, { id: userMsgId, role: 'user', text: trimmed }]);
      setDraft('');
      setLoading(true);

      try {
        const res = await fetch(`${BACKEND}/api/coach/message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ message: trimmed }),
        });
        if (!res.ok) {
          if (res.status === 401) {
            throw new Error('Please sign in to talk to the coach.');
          }
          if (res.status === 429) {
            throw new Error('You have reached today\u2019s coach limit. Come back tomorrow.');
          }
          throw new Error(`Coach is unavailable (HTTP ${res.status})`);
        }
        const data = await res.json();
        setConversation((prev) => [
          ...prev,
          { id: `c-${Date.now()}`, role: 'coach', text: data.message, response: data },
        ]);
      } catch (err) {
        console.warn('coach call failed', err);
        setConversation((prev) => [
          ...prev,
          {
            id: `e-${Date.now()}`,
            role: 'error',
            text: 'I had trouble reaching the coach right now. Please try again in a moment.',
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [draft, loading]
  );

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  if (!open || typeof document === 'undefined') return null;

  const overlay = (
    <div
      className="fixed inset-0 z-[2147483646] flex flex-col animate-fade-in"
      style={{ background: 'linear-gradient(160deg, #F7F3EA 0%, #E8DDCB 100%)' }}
      data-testid="coach-overlay"
    >
      {/* Header */}
      <div className="px-5 pt-6 pb-3 flex items-center justify-between border-b border-stone-200/60">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl btn-obsidian flex items-center justify-center shadow-md">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h2
              className="text-lg font-bold text-stone-800 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Reflection Coach
            </h2>
            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
              tell me what you&apos;re feeling
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/60 hover:bg-white text-stone-700 flex items-center justify-center transition-colors shadow-sm"
          aria-label="Close coach"
          data-testid="coach-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Conversation */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-5 py-4 space-y-3"
        data-testid="coach-conversation"
      >
        {conversation.length === 0 && !loading && (
          <div className="text-center pt-8 pb-4">
            <p className="text-sm text-stone-600 max-w-xs mx-auto leading-relaxed">
              Share what&apos;s on your heart. I&apos;ll meet you where you are with a calming affirmation,
              a breath, and a meditation that fits.
            </p>
            <div className="mt-5 flex flex-wrap gap-2 justify-center" data-testid="coach-suggestions">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => sendMessage(s)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/70 hover:bg-white text-stone-700 border border-stone-200/70 hover:border-stone-300 transition-colors shadow-sm"
                  data-testid={`coach-suggestion-${s.replace(/\W+/g, '-').toLowerCase()}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {conversation.map((msg) => {
          if (msg.role === 'user') {
            return (
              <div key={msg.id} className="flex justify-end" data-testid="coach-msg-user">
                <div className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-tr-md bg-stone-800 text-stone-50 text-sm shadow-sm">
                  {msg.text}
                </div>
              </div>
            );
          }
          if (msg.role === 'error') {
            return (
              <div key={msg.id} className="flex justify-start" data-testid="coach-msg-error">
                <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-tl-md bg-red-50 border border-red-200 text-red-700 text-sm">
                  {msg.text}
                </div>
              </div>
            );
          }
          // coach response
          const r = msg.response;
          return (
            <div key={msg.id} className="space-y-2" data-testid="coach-msg-coach">
              <div className="flex justify-start">
                <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-tl-md bg-white text-stone-800 text-sm shadow-sm border border-stone-200/60">
                  {msg.text}
                </div>
              </div>

              {r?.affirmation && (
                <div
                  className="px-4 py-3 rounded-2xl border border-amber-200/70 bg-gradient-to-br from-amber-50 to-orange-50 shadow-sm relative"
                  data-testid="coach-affirmation"
                >
                  <button
                    type="button"
                    onClick={() => setShareAffirmation(r.affirmation)}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/70 hover:bg-white text-amber-700 hover:text-[#D6AD60] flex items-center justify-center shadow-sm transition-colors"
                    aria-label="Share this affirmation"
                    data-testid="coach-affirmation-share"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                  </button>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-amber-700 mb-1 flex items-center gap-1 pr-8">
                    <Sparkles className="w-3 h-3" />
                    affirmation
                  </p>
                  <p
                    className="text-sm text-stone-800 leading-relaxed pr-8"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    &ldquo;{r.affirmation}&rdquo;
                  </p>
                </div>
              )}

              {r?.breathingExerciseId && (
                <Link
                  to={`/breathing?exercise=${r.breathingExerciseId}`}
                  onClick={onClose}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white hover:bg-stone-50 border border-stone-200/70 shadow-sm transition-colors"
                  data-testid="coach-breath-link"
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Wind className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500 mb-0.5">
                      breathing exercise
                    </p>
                    <p className="text-sm font-semibold text-stone-800 truncate">
                      {r.breathingExerciseName || r.breathingExerciseId}
                    </p>
                  </div>
                </Link>
              )}

              {r?.postId && (
                <Link
                  to={`/post/${r.postId}`}
                  onClick={onClose}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white hover:bg-stone-50 border border-stone-200/70 shadow-sm transition-colors"
                  data-testid="coach-post-link"
                >
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5 text-indigo-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500 mb-0.5">
                      meditation
                    </p>
                    <p className="text-sm font-semibold text-stone-800 truncate">
                      {r.postTitle || r.postId}
                    </p>
                  </div>
                </Link>
              )}
            </div>
          );
        })}

        {loading && (
          <div className="flex justify-start" data-testid="coach-loading">
            <div
              className="flex items-center gap-2.5 px-4 py-3 rounded-2xl rounded-tl-md bg-white text-stone-500 text-sm shadow-sm border border-stone-200/60"
              aria-live="polite"
              aria-label="Reflection Coach is typing"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#D6AD60]" />
              {/* Bouncing dots — reads as "typing" to users, huge perceived-latency win. */}
              <span className="flex items-center gap-1" aria-hidden>
                <span className="w-1.5 h-1.5 rounded-full bg-stone-400 coach-typing-dot" style={{ animationDelay: '0s' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-stone-400 coach-typing-dot" style={{ animationDelay: '0.15s' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-stone-400 coach-typing-dot" style={{ animationDelay: '0.30s' }} />
              </span>
              <span className="text-[13px] italic">reflecting on that…</span>
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={onSubmit}
        className="px-4 pb-6 pt-3 border-t border-stone-200/60 bg-white/40 backdrop-blur-md"
        data-testid="coach-composer"
      >
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="share what you're feeling…"
            rows={1}
            className="flex-1 resize-none px-4 py-2.5 rounded-2xl bg-white border border-stone-200 focus:border-stone-400 focus:outline-none text-sm text-stone-800 placeholder-stone-400 shadow-sm max-h-32"
            disabled={loading}
            data-testid="coach-input"
          />
          <button
            type="submit"
            disabled={!draft.trim() || loading}
            className="w-10 h-10 rounded-2xl btn-obsidian flex items-center justify-center shadow-md disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
            aria-label="Send"
            data-testid="coach-send"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <>
      {createPortal(overlay, document.body)}
      <ShareSheet
        open={!!shareAffirmation}
        onClose={() => setShareAffirmation(null)}
        content={
          shareAffirmation
            ? {
                kind: 'affirmation',
                id: 'coach-affirmation',
                title: 'A reflection from my coach',
                text: `"${shareAffirmation}" — via Reflections by Rowdy`,
                url: typeof window !== 'undefined' ? window.location.origin : '',
                recommendable: false,
              }
            : null
        }
      />
    </>
  );
};

export default ReflectionCoachOverlay;
